import { useMutation, useQueryClient } from "@tanstack/react-query";
import GlobalAppAPI from "../../api/GlobalAppAPI";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";

interface Props {
  appID: string;
  workspaceID: string;
  onSuccess?: () => void;
}

/**
 * Revokes a global app (deletes it)
 * Used in a global cloud to delete a global app
 */
const useRevokeGlobalApp = () => {
  const client = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);

  const revokeGlobalApp = useMutation({
    mutationFn: async ({ appID, workspaceID }: Props) => {
      newToastEvent("success", "Revoking global app...", "RevokingGlobalApp", {
        loading: true,
        autoClose: false,
      });

      return GlobalAppAPI.revokeGlobalApp(appID, workspaceID);
    },
    onSuccess: (response, { onSuccess }) => {
      client.invalidateQueries({ queryKey: ["workspace", workspaceID] });
      client.invalidateQueries({ queryKey: ["cloudApps"] });

      updateToastEvent(
        "success",
        `Successfully revoked global app.`,
        "RevokingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );

      callFunctionIfExists(onSuccess);
    },
    onError: (error) => {
      updateToastEvent(
        "error",
        `Failed to revoke global app! ${getErrorMessage(error)}`,
        "RevokingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );
    },
  });

  return {
    revokeGlobalApp: revokeGlobalApp.mutate,
    revokingApp: revokeGlobalApp.isPending,
  };
};

export default useRevokeGlobalApp;
